import { StrictMode, useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { AuthPage } from "./AuthPage";
import { authClient } from "./lib/auth-client";

type AuthState = "loading" | "authenticated" | "unauthenticated";

interface User {
  id: string;
  email: string;
  name: string;
}

function UserMenu({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const initial = (user.name || user.email)[0]?.toUpperCase() ?? "?";

  return (
    <div ref={menuRef} style={{ position: "fixed", top: 20, right: 20, zIndex: 100 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={user.email}
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          border: "1px solid var(--border)",
          background: "var(--card)",
          color: "var(--foreground)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "inherit",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        {initial}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            padding: 6,
            minWidth: 180,
          }}
        >
          <div
            style={{
              padding: "6px 10px 10px",
              borderBottom: "1px solid var(--border)",
              marginBottom: 4,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>
              {user.name || "Account"}
            </div>
            <div style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 1 }}>
              {user.email}
            </div>
          </div>
          <button
            onClick={async () => { setOpen(false); onSignOut(); }}
            style={{
              display: "block",
              width: "100%",
              padding: "6px 10px",
              background: "transparent",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              textAlign: "left",
              fontSize: 13,
              fontFamily: "inherit",
              color: "var(--foreground)",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--muted)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Root() {
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    authClient
      .getSession()
      .then(({ data }) => {
        if (data?.user) {
          setUser(data.user as User);
          setAuthState("authenticated");
        } else {
          setAuthState("unauthenticated");
        }
      })
      .catch(() => setAuthState("unauthenticated"));
  }, []);

  const handleSignOut = async () => {
    await authClient.signOut();
    setUser(null);
    setAuthState("unauthenticated");
  };

  if (authState === "loading") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "var(--background)",
        }}
      >
        <div
          style={{
            width: 20,
            height: 20,
            border: "2px solid var(--border)",
            borderTopColor: "var(--foreground)",
            borderRadius: "50%",
            animation: "spin 0.6s linear infinite",
          }}
        />
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return (
      <AuthPage
        onAuth={(u) => {
          setUser(u);
          setAuthState("authenticated");
        }}
      />
    );
  }

  return <App user={user!} onSignOut={handleSignOut} />;
}

const elem = document.getElementById("root")!;

// https://bun.com/docs/bundler/hot-reloading#import-meta-hot-data
(import.meta.hot.data.root ??= createRoot(elem)).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
